import * as React from 'react'
import {
  generatePath,
  Navigate,
  Route,
  Routes,
  useParams,
} from 'react-router-dom'
import {
  RequireAuth,
  RequireGlobalAdmin,
  RequireValidCompanyId,
} from 'app/components/application/protectedRoute'
import { ROUTES } from 'app/constants/routes'
import NotFoundRaw from 'app/components/application/notFound'
import { CompanySelectionRoute } from '@/app/features/company-selection/CompanySelectionRoute'
import AdministrationPanelRaw from 'app/containers/administration/administrationPanelContainer'
import GenericErrorPresenterRaw from 'app/containers/application/genericErrorPresenterContainer'
import { AdminEditCompanyPage } from '@/modules/adminPanel/companies/AdminEditCompanyPage'
import { AdminEditUserPage } from '@/modules/adminPanel/users/AdminEditUserPage'
import { withRouter } from 'app/navigation/withRouter'

// These legacy containers read `this.props.history/match/location` directly.
// In v5 those props came from <Route component={...}/>; v6 <Route element>
// doesn't inject them, so wrap via the withRouter shim.
const NotFound = withRouter(NotFoundRaw as any)
const AdministrationPanel = withRouter(AdministrationPanelRaw as any)
const GenericErrorPresenter = withRouter(GenericErrorPresenterRaw as any)
import {
  SettingsModule,
  settingsPath,
  HomeModule,
  ImportsModule,
  importsPath,
  homePath,
  CoreModule,
  corePath,
  DriversModule,
  driversPath,
  PrintoutModule,
  printoutsPath,
  ReportsModule,
  reportsPath,
  timetablePath,
  TimetableModule,
  VehiclesModule,
  vehiclesPath,
  DatabasesModule,
  databasesPath,
  AddCompanyModule,
  addCompanyPath,
  ProfileModule,
  profilePath,
  BillingModule,
  billingPath,
} from '@/modules'
import { UsersModule, usersPath } from '@/modules/users'
import {} from '@/modules/core/'
import { ProtectedApp } from '@/ProtectedApp'
import { SubscriptionModule } from '@/modules/subscription'
import { PayrollTestPage } from '@/modules/payrollTest/PayrollTestPage'
import { ServiceAccountOwnerModule } from '@/modules/serviceAccount/ServiceAccountOwnerModule'
import { AppLayoutDahboardWithNav } from '@/modules/shared/layouts/AppLayoutDahboardWithNav'
import { useQueryCompanies } from '@/modules/shared/query-client'
import { isUnusableServiceCompany } from '@/app/constants/companyType'

const mySubscriptionPath = '/et/company/:companyId/my-subscription'
const payrollTestPath = '/et/company/:companyId/payrollTest'
const serviceAccountPath = '/et/company/:companyId/serviceAccount'

function EtCompanyRootRedirect() {
  const { companyId } = useParams<{ companyId: string }>()
  const companies = useQueryCompanies({ includeDeleted: false })
  // Wait for the list: sending a no-database service company to /dashboard first
  // would fire the 500 the modal is meant to prevent.
  if (companies.isLoading) return null
  const target = companies.data?.find(
    (c) => c.id.toLowerCase() === companyId?.toLowerCase(),
  )
  const path = isUnusableServiceCompany(target)
    ? '/et/company/:companyId/serviceAccount'
    : '/et/company/:companyId/dashboard'
  return <Navigate to={generatePath(path, { companyId: companyId! })} replace />
}

const withCompanyGuard = (element: React.ReactNode) => (
  <RequireValidCompanyId>{element}</RequireValidCompanyId>
)

export function ProtectedAppRoutes() {
  return (
    <ProtectedApp>
      <Routes>
        <Route
          path="/"
          element={<Navigate to={ROUTES.CompanySelection.NoCompany} replace />}
        />

        <Route
          path="/logoutComplete"
          element={
            <div>
              <h1>--logoutComplete--</h1>
            </div>
          }
        />

        {/* ET Modules Routes */}
        <Route
          path="/et/company/:companyId"
          element={withCompanyGuard(<EtCompanyRootRedirect />)}
        />
        <Route
          path={`${settingsPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <SettingsModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${usersPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <UsersModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${driversPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <DriversModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${vehiclesPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <VehiclesModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${printoutsPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <PrintoutModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${timetablePath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <TimetableModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${reportsPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <ReportsModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${importsPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <ImportsModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${databasesPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <DatabasesModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${addCompanyPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <AddCompanyModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${billingPath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <BillingModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={payrollTestPath}
          element={withCompanyGuard(
            <RequireAuth>
              <RequireGlobalAdmin>
                <AppLayoutDahboardWithNav>
                  <div className="tw-flex-1">
                    <PayrollTestPage />
                  </div>
                </AppLayoutDahboardWithNav>
              </RequireGlobalAdmin>
            </RequireAuth>,
          )}
        />
        <Route
          path={mySubscriptionPath}
          element={withCompanyGuard(
            <RequireAuth>
              <SubscriptionModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={serviceAccountPath}
          element={withCompanyGuard(
            <RequireAuth>
              <ServiceAccountOwnerModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${profilePath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <ProfileModule />
            </RequireAuth>,
          )}
        />
        <Route
          path={`${homePath}/*`}
          element={withCompanyGuard(
            <RequireAuth>
              <HomeModule />
            </RequireAuth>,
          )}
        />
        <Route path={`${corePath}/*`} element={<CoreModule />} />

        <Route
          path={ROUTES.CompanySelection.NoCompany}
          element={
            <RequireAuth>
              <CompanySelectionRoute />
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.Company}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdministrationPanel />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.CompanyTab}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdministrationPanel />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.NoCompany}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdministrationPanel />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.NoCompanyTab}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdministrationPanel />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.EditCompany}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdminEditCompanyPage />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.EditUser}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdminEditUserPage />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />
        <Route
          path={ROUTES.AdministrationPanel.EditUserNoCompany}
          element={
            <RequireAuth>
              <RequireGlobalAdmin>
                <AdminEditUserPage />
              </RequireGlobalAdmin>
            </RequireAuth>
          }
        />

        <Route
          path={ROUTES.GenericErrorWithCompany}
          element={<GenericErrorPresenter />}
        />
        <Route path={ROUTES.GenericError} element={<GenericErrorPresenter />} />
        <Route path={ROUTES.NotFound} element={<NotFound />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ProtectedApp>
  )
}
